"use client";

import { useState } from "react";
import Link from "next/link";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { sendContactEmail, EmailJsConfigError } from "@/lib/contactEmail";

/**
 * The home page's contact band: a short pitch on the left with three promises,
 * and a four-field form on the right that goes to the same inbox as /demo.
 * Kept to four fields on purpose; the demo page asks for the rest.
 */

const POINTS = [
  { title: "A sandbox on your protocol", body: "Set up before the call, with no scoping call first." },
  { title: "A live walkthrough", body: "On your own protocol, not a slide deck." },
  { title: "A team that owns the outcome", body: "The founding team on the call. A direct relationship." },
];

const EMPTY = { name: "", company: "", email: "", needs: "" };

export function ContactBand() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await sendContactEmail({
        name: form.name,
        email: form.email,
        company: form.company,
        message: `Home page contact form\n\nStudy and data needs:\n${form.needs}`,
      });
      toast.success("Thanks, we have it", {
        description: "The team will reply within one business day.",
      });
      setForm(EMPTY);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof EmailJsConfigError ? "Form not configured yet" : "Couldn't send that", {
        description: "Please email hello@trialion.com directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="cb">
      <div className="cb-pitch" data-reveal>
        <p className="cb-eyebrow">For sponsors and CROs</p>
        <h2 className="cb-title">
          Your protocol.
          <br /> Our <span className="cb-accent">build.</span>
        </h2>
        <p className="cb-lede">Running a study in India or beyond? Let&rsquo;s draft it on your protocol, live.</p>
        <p className="cb-note">
          Talk directly with the founding team about your studies, timelines and data needs.
        </p>
        <ul className="cb-points">
          {POINTS.map((p) => (
            <li key={p.title}>
              <ArrowUpRight className="cb-point-icon" strokeWidth={1.5} aria-hidden="true" />
              <span>
                <strong>{p.title}</strong>
                <span>{p.body}</span>
              </span>
            </li>
          ))}
        </ul>
        <Link className="cb-link" href="/demo">
          Book a conversation
          <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
        </Link>
      </div>

      {/* data-clarity-mask: session recording never sees what is typed here. */}
      <form className="cb-form" onSubmit={submit} data-clarity-mask="True" data-reveal>
        <p className="cb-eyebrow">Tell us what you need</p>
        <h3 className="cb-form-title">Tell us about your study.</h3>
        <label className="cb-field">
          <span>Your name *</span>
          <input required autoComplete="name" placeholder="Full name" value={form.name} onChange={set("name")} />
        </label>
        <div className="cb-row">
          <label className="cb-field">
            <span>Company *</span>
            <input required autoComplete="organization" placeholder="Company name" value={form.company} onChange={set("company")} />
          </label>
          <label className="cb-field">
            <span>Work email *</span>
            <input
              required
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={form.email}
              onChange={set("email")}
            />
          </label>
        </div>
        <label className="cb-field">
          <span>Study &amp; data needs *</span>
          <textarea
            required
            rows={4}
            placeholder="Phase, therapy area, number of sites and subjects, and when you plan to start"
            value={form.needs}
            onChange={set("needs")}
          />
        </label>
        <button className="cb-submit" type="submit" disabled={sending}>
          {sending ? "Sending…" : "Discuss your study"}
          {sending ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
          )}
        </button>
        <p className="cb-alt">
          Prefer email? <a href="mailto:hello@trialion.com">hello@trialion.com</a>
        </p>
      </form>
    </div>
  );
}
